import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  EMERGENCY_MAX_SPAN_DAYS,
  LABEL_MAX,
  MESSAGE_MAX,
  findUnknownPlaceholders,
} from '@/lib/bot-service-hours';
import { NumberMultiSelect } from './number-multi-select';
import { OutOfHoursMessageEditor } from './out-of-hours-message-editor';

// Start/edit dialog for an emergency override ("close everything from now until tomorrow and
// send this"). Times are entered as datetime-local values interpreted in the org's default
// timezone and stored as UTC ISO. Unlike the rest of the editor this commits IMMEDIATELY —
// the section PUTs against a fresh GET the moment the primary button is pressed.

const LOCAL_FORMAT = "yyyy-LL-dd'T'HH:mm";

const FieldLabel = ({ children }) => (
  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
    {children}
  </span>
);

export function EmergencyDialog({
  open,
  onOpenChange,
  emergency,
  gateways,
  placeholders,
  timezone,
  busy = false,
  onSave,
}) {
  const zone = timezone || 'UTC';
  const [label, setLabel] = useState('');
  const [gatewayBotIds, setGatewayBotIds] = useState(null);
  const [fromLocal, setFromLocal] = useState('');
  const [untilLocal, setUntilLocal] = useState('');
  const [openEnded, setOpenEnded] = useState(false);
  const [mode, setMode] = useState('reply');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    if (emergency) {
      setLabel(emergency.label || '');
      setGatewayBotIds(emergency.gatewayBotIds ?? null);
      setFromLocal(DateTime.fromISO(emergency.from).setZone(zone).toFormat(LOCAL_FORMAT));
      setOpenEnded(emergency.until == null);
      setUntilLocal(
        emergency.until
          ? DateTime.fromISO(emergency.until).setZone(zone).toFormat(LOCAL_FORMAT)
          : DateTime.now().setZone(zone).plus({ hours: 4 }).toFormat(LOCAL_FORMAT),
      );
      setMode(emergency.mode || 'reply');
      setMessage(emergency.message || '');
    } else {
      setLabel('');
      setGatewayBotIds(null);
      setFromLocal(DateTime.now().setZone(zone).toFormat(LOCAL_FORMAT));
      setUntilLocal(DateTime.now().setZone(zone).plus({ hours: 4 }).toFormat(LOCAL_FORMAT));
      setOpenEnded(false);
      setMode('reply');
      setMessage('');
    }
  }, [open, emergency, zone]);

  const fromDT = fromLocal ? DateTime.fromISO(fromLocal, { zone }) : null;
  const untilDT = !openEnded && untilLocal ? DateTime.fromISO(untilLocal, { zone }) : null;
  const fromISO = fromDT?.isValid ? fromDT.toUTC().toISO() : null;
  const untilISO = openEnded ? null : untilDT?.isValid ? untilDT.toUTC().toISO() : null;

  const labelLength = Array.from(label.trim()).length;
  const labelError =
    labelLength === 0 ? 'Give the override a label.' : labelLength > LABEL_MAX ? `At most ${LABEL_MAX} characters.` : '';
  let rangeError = '';
  if (!fromISO) rangeError = 'Enter a start time.';
  else if (!openEnded && !untilISO) rangeError = 'Enter an end time, or tick "until further notice".';
  else if (untilISO && untilISO <= fromISO) rangeError = 'The override must end after it starts.';
  else if (untilISO && untilDT.diff(fromDT, 'days').days > EMERGENCY_MAX_SPAN_DAYS)
    rangeError = `At most ${EMERGENCY_MAX_SPAN_DAYS} days — tick "until further notice" instead.`;
  const unknownTokens = findUnknownPlaceholders(message, placeholders);
  const messageError =
    mode === 'reply' && !message.trim()
      ? 'A message is required in reply mode.'
      : Array.from(message).length > MESSAGE_MAX
        ? `At most ${MESSAGE_MAX} characters.`
        : unknownTokens.length > 0
          ? `Unknown placeholder ${unknownTokens.map((t) => `{{${t}}}`).join(', ')}.`
          : '';
  const scopeError =
    Array.isArray(gatewayBotIds) && gatewayBotIds.length === 0 ? 'Select at least one number.' : '';
  const canSave = !labelError && !rangeError && !messageError && !scopeError && !busy;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      id: emergency?.id || '',
      label: label.trim(),
      gatewayBotIds,
      from: fromISO,
      until: untilISO,
      mode,
      message,
      // Server-stamped, immutable by id — sent back as loaded (null on a new entry).
      createdBy: emergency?.createdBy || '',
      createdAt: emergency?.createdAt ?? null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{emergency ? 'Edit emergency override' : 'Start emergency override'}</DialogTitle>
          <DialogDescription>
            Force the bots closed for a while — an aircon failure, a power cut. Applies
            immediately when saved, on top of every other rule.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3.5">
          <label className="flex flex-col gap-1">
            <FieldLabel>Label</FieldLabel>
            <Input
              value={label}
              maxLength={LABEL_MAX}
              placeholder="e.g. Aircon failure"
              onChange={(event) => setLabel(event.target.value)}
            />
            {label && labelError ? <p className="text-[11px] text-destructive">{labelError}</p> : null}
          </label>

          <div className="flex flex-col gap-1">
            <FieldLabel>Applies to</FieldLabel>
            <div>
              <NumberMultiSelect
                gateways={gateways}
                value={gatewayBotIds}
                onChange={setGatewayBotIds}
              />
            </div>
            {scopeError ? <p className="text-[11px] text-destructive">{scopeError}</p> : null}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex w-52 flex-col gap-1">
              <FieldLabel>From</FieldLabel>
              <Input
                type="datetime-local"
                value={fromLocal}
                onChange={(event) => setFromLocal(event.target.value)}
                className="h-8 text-xs"
              />
            </label>
            <label className="flex w-52 flex-col gap-1">
              <FieldLabel>Until</FieldLabel>
              <Input
                type="datetime-local"
                value={untilLocal}
                disabled={openEnded}
                onChange={(event) => setUntilLocal(event.target.value)}
                className="h-8 text-xs"
              />
            </label>
            <label className="flex h-8 cursor-pointer items-center gap-1.5 text-xs text-foreground">
              <input
                type="checkbox"
                checked={openEnded}
                onChange={(event) => setOpenEnded(event.target.checked)}
                className="size-3.5 rounded border-border accent-primary"
              />
              Until further notice
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Times are in {String(zone).replace(/_/g, ' ')}.
          </p>
          {rangeError ? <p className="text-[11px] text-destructive">{rangeError}</p> : null}

          <OutOfHoursMessageEditor
            mode={mode}
            message={message}
            placeholders={placeholders}
            errors={{ message: messageError }}
            onChange={(patch) => {
              if ('mode' in patch) setMode(patch.mode);
              if ('message' in patch) setMessage(patch.message);
            }}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave}>
            {busy ? 'Saving…' : emergency ? 'Save changes' : 'Start emergency'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
